import { createAuthenticatedClient } from "../authenticatedClient";
import { AGENT_REGISTRY_API_VERSION, API_BASE_URL } from "../config";
import { safeValidate } from "../safeValidate";
import {
  RequestListResponseSchema,
  RequestListResponse,
  UserListResponseSchema,
  UserListResponse,
  PlatformListResponseSchema,
  PlatformListResponse,
  AgentListResponseSchema,
  AgentListResponse,
} from "../schemas/agentRequest.schema";

const isMfeMode =
  typeof window !== "undefined" &&
  (window.location.hostname === "loopback" ||
    window.location.pathname.startsWith("/ECMv6") ||
    window.location.pathname.startsWith("/intelligence"));

const omniflowBasePath = isMfeMode
  ? `${API_BASE_URL}/omniflow/api/v1`
  : `${API_BASE_URL}/api/omniflow/api/v1`;

const requestClient = createAuthenticatedClient(omniflowBasePath);

// REMOVE LATER = THIS IS FROM AGENT MANAGEMENT
const agentClient = createAuthenticatedClient(
  `${API_BASE_URL}/agent/${AGENT_REGISTRY_API_VERSION}/registry`,
);

/** @description Parameters for the request list API call. */
export interface GetRequestListParams {
  searchQuery?: string;
  filter?: Record<string, string[]>;
  limit?: number;
  cursor?: string | null;
  sort?: {
    field: string;
    order: "asc" | "desc";
  };
  view?: string;
}

/** @description Parameters for the user list search API call. */
export interface GetUserListParams {
  searchText?: string;
  pageSize?: number;
}

/** @description Parameters for the platform list search API call. */
export interface GetPlatformListParams {
  searchText?: string;
}

/** @description Parameters for the agent list API call. */
export interface GetAgentListParams {
  searchQuery: string;
  criticality: string[];
  platforms: string[];
  sort: { field: string; direction: "asc" | "desc" };
  pagination: {
    page: number;
    limit: number;
  };
}

/** @description A single access request item in the submit payload. */
export interface AccessRequestItem {
  requestType: "REQUEST_NEW_ACCESS" | "MODIFY_ACCESS" | "REMOVE_ACCESS";
  beneficiaryKeys: number[];
  beneficiaryType: "USER";
  accessKeys: (string | number)[];
  accessType: "AGENT";
  platform: string;
  startDate?: string;
  endDate?: string;
  permissions?: string[];
  businessJustification: string;
}

/** @description Payload for submitting access requests. */
export interface SubmitAccessRequestParams {
  requestOrigin: "API";
  accessRequests: AccessRequestItem[];
}

const URL_USERS_LIST = "/users";
const URL_REQUESTS_LIST = "/requests/list";
const URL_ACCESS_REQUESTS = "/access-requests";

/**
 * @description API client for agent request endpoints.
 */
export const agentRequestApi = {
  getRequestList: async (payload: GetRequestListParams) => {
    const data = await requestClient.post<RequestListResponse>(
      URL_REQUESTS_LIST,
      payload,
    );

    return safeValidate(RequestListResponseSchema, data, "getRequestList");
  },

  getUserList: async (params: GetUserListParams) => {
    const { pageSize = 1, searchText = "" } = params;

    const query = new URLSearchParams();

    if (pageSize) {
      query.set("pageSize", String(pageSize));
    }

    if (searchText.trim().length > 0) {
      query.set("searchText", searchText.trim());
    }

    const url = `${URL_USERS_LIST}?${query.toString()}`;
    const data = await requestClient.get<UserListResponse>(url, {
      headers: { "X-Tenant-Id": "123", "X-Requestor-Id": "1" },
    });

    return safeValidate(UserListResponseSchema, data, "getUserList");
  },

  getPlatformList: async (params?: GetPlatformListParams) => {
    const { searchText = "" } = params ?? {};

    const query = new URLSearchParams();

    if (searchText.trim().length > 0) {
      query.set("searchText", searchText.trim());
    }

    const url = `/platforms?${query.toString()}`;
    const data = await requestClient.get<PlatformListResponse>(url, {
      headers: { "X-Tenant-Id": "123", "X-Requestor-Id": "1" },
    });

    return safeValidate(PlatformListResponseSchema, data, "getPlatformList");
  },

  getAgentList: async (payload: GetAgentListParams) => {
    const data = await agentClient.post<AgentListResponse>(
      "/agents/search",
      payload,
    );

    return safeValidate(AgentListResponseSchema, data, "getAgentList");
  },

  submitAccessRequest: async (payload: SubmitAccessRequestParams) => {
    const data = await requestClient.post(URL_ACCESS_REQUESTS, payload, {
      headers: { "X-Tenant-Id": "123", "X-Requestor-Id": "1" },
    });

    return data;
  },
};
